/**
 * downloadTimelineAudioClip — download the SOURCE audio file for a timeline
 * audio clip (extracted / uploaded / split).
 *
 * Reuses the SAME proven asset path as playback / waveform: prefer the
 * already-preloaded blob (keyed by sourcePath), else fetch via the
 * credentialless media path. A same-origin `blob:` URL means the `<a download>`
 * filename is honored regardless of the source origin's Content-Disposition
 * (presigned GCS / R2 / Supabase included). Trim, speed, volume, and mute are
 * intentionally NOT applied — this is the raw source asset (see the button
 * tooltip copy in AdjustPanel).
 *
 * All I/O + user-facing concerns are injected so this is unit-testable without
 * mounting the modal (the modal wires the real deps).
 */
import type { AssetDownloadAttempt } from '../../../../analytics/assetDownloadAnalytics'
import type { TimelineAudioClip } from './types'
import { deriveAudioClipFilename } from './deriveAudioClipFilename'

/** Where the downloaded bytes came from. */
export type DownloadSource = 'cached' | 'fetched'

/**
 * Structured NAD events for a clip download. Discriminated on `event` so the
 * required fields per event are compile-time enforced — and, critically, so no
 * free-form response body (which for signed GCS/R2 URLs can echo canonical
 * request or credential material) can be logged. Detailed error prose goes to
 * the user toast only; logs carry stable enums (`reason`, `status`, `errorName`).
 */
export type AudioDownloadLogEvent =
  | { event: 'requested'; clipId: string; filename: string; source: DownloadSource }
  | { event: 'download-triggered'; clipId: string; source: DownloadSource }
  | {
      event: 'failed'
      clipId: string
      reason: 'no-project' | 'empty-source-path' | 'http-error' | 'exception'
      source?: DownloadSource
      status?: number
      errorName?: string
    }

export interface DownloadTimelineAudioClipDeps {
  /** Active project id (undefined → cannot resolve asset URLs). */
  projectId: string | undefined
  /** Preloader blob-URL lookup (stable identity). Returns object URL or null. */
  getBlobUrl: (sourcePath: string) => string | null
  /**
   * Fetch the media asset. Wraps `fetchMediaBlob(projectId, sourcePath)` — note
   * it resolves with the `Response` even on non-OK status (does NOT throw), so
   * this function checks `.ok` explicitly.
   */
  fetchMedia: (projectId: string, sourcePath: string) => Promise<Response>
  /** Structured NAD event sink (feature-tagged logger, bound with nodeId). */
  log: (fields: AudioDownloadLogEvent) => void
  /** User-facing error toast. */
  toastError: (message: string) => void
  /**
   * RFC-1577 `asset_download_action`: start one analytics attempt (records
   * `initiated`) once the guards pass and the handoff begins. Optional so the
   * helper stays usable without analytics.
   */
  beginAttempt?: (filename: string) => AssetDownloadAttempt
  /** Pre-localized message strings. */
  messages: {
    noProject: string
    unknownError: string
    downloadFailed: (message: string) => string
  }
}

/** Delay before revoking our locally-created object URL — long enough for the
 *  browser to consume it. Mirrors the 60s cleanup window in
 *  useNodeActions.handleDownload. */
export const LOCAL_URL_REVOKE_DELAY_MS = 60_000

/** Common JSON error-body shapes returned by the asset backends. */
interface JsonErrorBody {
  detail?: string
  message?: string
  error?: string
}

/** Extract a human `detail`/`message`/`error` string from a parsed JSON error
 *  body, or null. Guards non-object JSON (numbers, strings, null) at runtime. */
function readJsonErrorDetail(trimmed: string): string | null {
  let body: JsonErrorBody | null = null
  try {
    const raw = JSON.parse(trimmed)
    if (raw && typeof raw === 'object') {
      body = raw
    }
  } catch {
    return null
  }
  if (!body) return null
  const detail = body.detail ?? body.message ?? body.error
  return typeof detail === 'string' && detail.trim() ? detail.trim() : null
}

/**
 * Build a concise error string from a non-OK Response. Reads the body once and
 * surfaces a JSON `detail`/`message`/`error` field when present, else the
 * (bounded) text body, always prefixed with the HTTP status.
 */
async function describeResponseError(response: Response): Promise<string> {
  const status = `HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`
  try {
    const text = await response.text()
    const trimmed = text.trim()
    if (!trimmed) return status
    const jsonDetail = readJsonErrorDetail(trimmed)
    if (jsonDetail) return `${status}: ${jsonDetail}`
    return `${status}: ${trimmed.slice(0, 300)}`
  } catch {
    return status
  }
}

/** Create a hidden anchor, click it to trigger the download, and always remove
 *  it — even if `click()` throws. */
function triggerAnchorDownload(href: string, filename: string): void {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  try {
    a.click()
  } finally {
    a.remove()
  }
}

export async function downloadTimelineAudioClip(
  clip: Pick<TimelineAudioClip, 'id' | 'sourcePath'>,
  deps: DownloadTimelineAudioClipDeps,
): Promise<void> {
  const { projectId, getBlobUrl, fetchMedia, log, toastError, beginAttempt, messages } = deps
  const filename = deriveAudioClipFilename(clip.sourcePath)

  // Guards — logged as failures with a stable `reason` (no `source` resolved
  // yet). NOTE: never log clip.sourcePath — it may be a signed URL. `filename`
  // is a sanitized basename and `source`/`reason` are stable enums; all safe.
  if (!projectId) {
    log({ event: 'failed', clipId: clip.id, reason: 'no-project' })
    toastError(messages.noProject)
    return
  }
  const sourcePath = clip.sourcePath
  if (!sourcePath || sourcePath.trim() === '') {
    log({ event: 'failed', clipId: clip.id, reason: 'empty-source-path' })
    toastError(messages.downloadFailed(messages.unknownError))
    return
  }

  // `source` stays undefined until the cache lookup resolves it, so a failure
  // thrown by `getBlobUrl` itself (kept inside the try so it can never become a
  // silent unhandled rejection at the `void` caller) is logged WITHOUT a
  // misleading `source` — no fetch occurred.
  let source: DownloadSource | undefined
  // RFC-1577: one attempt per click past the guards; `stage` names what a
  // thrown failure was doing (never a message).
  const attempt = beginAttempt?.(filename)
  let stage: 'unknown' | 'network_error' | 'handoff_failed' = 'unknown'
  try {
    const cachedBlobUrl = getBlobUrl(sourcePath)
    source = cachedBlobUrl ? 'cached' : 'fetched'
    log({ event: 'requested', clipId: clip.id, filename, source })

    if (cachedBlobUrl) {
      // The preloader owns this object URL's lifecycle (it revokes on unmount /
      // path removal). Do NOT revoke it here.
      stage = 'handoff_failed'
      triggerAnchorDownload(cachedBlobUrl, filename)
      log({ event: 'download-triggered', clipId: clip.id, source })
      return
    }

    stage = 'network_error'
    const response = await fetchMedia(projectId, sourcePath)
    if (!response.ok) {
      attempt?.failed('http_error')
      // Surface the detailed reason to the USER (toast) but keep it OUT of NAD
      // logs — storage 4xx/5xx bodies (e.g. GCS/R2 signature errors) can echo
      // canonical-request or credential material. Log only stable fields.
      const detail = await describeResponseError(response)
      log({ event: 'failed', clipId: clip.id, source, reason: 'http-error', status: response.status })
      toastError(messages.downloadFailed(detail))
      return
    }
    const blob = await response.blob()
    stage = 'handoff_failed'
    const localUrl = URL.createObjectURL(blob)
    try {
      triggerAnchorDownload(localUrl, filename)
      log({ event: 'download-triggered', clipId: clip.id, source })
    } finally {
      // Always schedule revocation of OUR locally-created URL — even if the
      // anchor trigger threw. Browser download completion is not observable, so
      // we neither log "completed" nor revoke synchronously.
      window.setTimeout(() => URL.revokeObjectURL(localUrl), LOCAL_URL_REVOKE_DELAY_MS)
    }
  } catch (err) {
    // JS exceptions (network / DOM / anchor). Log the error CLASS only — never a
    // response body or raw message — and surface the message to the user toast.
    const message = err instanceof Error ? err.message : messages.unknownError
    const errorName = err instanceof Error ? err.name : 'unknown'
    attempt?.failed(stage)
    log({ event: 'failed', clipId: clip.id, source, reason: 'exception', errorName })
    toastError(messages.downloadFailed(message))
  }
}
